import { Discount, OrderStatus, Prisma } from "@prisma/client";
import { completeBuyableItemValues } from "./buyable-item.types";
import {
  CommercialisationMeetingValues,
  CommercialisationTaskCommentValues,
} from "./commercialisation.types";
import { ComputablePricing, DetailedFinalPrice } from "./pricing.types";
import { TicketBuyerLimits } from "./ticket-limits.types";

export const TICKET_NOT_AVAILABLE_YET =
  "Ce billet n'est pas encore disponible à la vente";
export const TICKET_NOT_AVAILABLE_ANYMORE =
  "Ce billet n'est plus disponible à la vente";
export const TICKET_QUANTITY_TOO_LOW =
  "Le nombre de billet restant n'est pas suffisant";
export const TICKET_GROUP_MIN_SIZE_NOT_REACHED =
  "La quantité minimale pour ce billet n'est pas atteinte";
export const TICKET_USER_LIMIT_REACHED =
  "Vous avez atteint la limite d'achat pour ce billet";
export const TICKET_NOT_ELIGIBLE = "Vous n'êtes pas éligible à ce billet";
export const BADGE_NOT_AVAILABLE_YET =
  "Ce badge n'est pas encore disponible à la vente";
export const BADGE_NOT_AVAILABLE_ANYMORE =
  "Ce badge n'est plus disponible à la vente";
export const BADGE_QUANTITY_TOO_LOW =
  "Le nombre de badge restant n'est pas suffisant";
export const BADGE_GROUP_MIN_SIZE_NOT_REACHED =
  "La quantité minimale pour ce badge n'est pas atteinte";

export const TicketError = [
  TICKET_QUANTITY_TOO_LOW,
  TICKET_NOT_AVAILABLE_YET,
  TICKET_NOT_AVAILABLE_ANYMORE,
  TICKET_GROUP_MIN_SIZE_NOT_REACHED,
  TICKET_USER_LIMIT_REACHED,
  TICKET_NOT_ELIGIBLE,
] as const;
export type TicketError = (typeof TicketError)[number];

export const BadgeError = [
  BADGE_QUANTITY_TOO_LOW,
  BADGE_NOT_AVAILABLE_YET,
  BADGE_NOT_AVAILABLE_ANYMORE,
  BADGE_GROUP_MIN_SIZE_NOT_REACHED,
] as const;
export type BadgeError = (typeof BadgeError)[number];

export const getBasketItemTypeValues =
  Prisma.validator<Prisma.BasketItemDefaultArgs>()({
    select: {
      id: true,
      buyableItem: completeBuyableItemValues,
      createdAt: true,
      updatedAt: true,
      expirationCountdown: true,
    },
  });

export type GetBasketItem = Prisma.BasketItemGetPayload<
  typeof getBasketItemTypeValues
>;

export const simpleBasketTypeValues =
  Prisma.validator<Prisma.BasketDefaultArgs>()({
    select: {
      id: true,
      type: true,
      discountCode: {
        select: {
          id: true,
          name: true,
          isCumulable: true,
          quantityLeft: true,
          isQuantityInfinite: true,
          maxUsesPerVisitor: true,
          eventId: true,
          pricings: {
            select: {
              pricingId: true,
              quantityLeft: true,
              pricing: {
                select: {
                  ticketType: {
                    select: {
                      id: true,
                    },
                  },
                  badgeType: {
                    select: {
                      id: true,
                    },
                  },
                  backofficePrestation: {
                    select: {
                      type: true,
                    },
                  },
                },
              },
            },
          },
          ticketingId: true,
          validityDates: true,
          organiserId: true,
          discount: { select: { type: true, value: true } },
        },
        orderBy: [
          { discount: { type: "desc" } },
          { discount: { value: "desc" } },
        ],
        where: {
          disabledAt: null,
          deletedAt: null,
        },
      },
      basketItem: {
        select: getBasketItemTypeValues.select,
        orderBy: {
          expirationCountdown: "desc",
        },
      },
      isExpired: true,
      hasExtendedExpirationTime: true,
      comments: {
        orderBy: { createdAt: "asc" },
        select: CommercialisationTaskCommentValues.select,
      },
      meetings: {
        select: CommercialisationMeetingValues.select,
        orderBy: {
          updatedAt: "desc",
        },
      },
    },
  });

export type SimpleBasket = Prisma.BasketGetPayload<
  typeof simpleBasketTypeValues
>;

export type BuyableItemPricingBreakdown = GetBasketItem &
  DetailedFinalPrice & {
    id: string;
    exclTaxesCodeDiscount?: number;
    basePricing: ComputablePricing;
  };

export type BuyableItemsPricingBreakdown = {
  itemBreakdowns: BuyableItemPricingBreakdown[];
  codeDiscount: number;
  exclTaxesCodeDiscount: number;
  degressiveDiscount: number;
  finalPrice: number;
  exclTaxesTotal: number;
  basePrice: number;
  baseExclTaxesPrice: number;
  expirationCountdown: Date;
};

export type BasketItemError = TicketError | BadgeError;

export type GetBasketItemWithError = GetBasketItem & {
  error: BasketItemError | null;
};

// Body of the conflict the basket validation answers with, one entry per refused line
export type BasketErrorsConflict = {
  message?: string;
  basketErrors?: GetBasketItemWithError[];
};

export type GetUserBasket = Omit<SimpleBasket, "basketItem"> &
  BuyableItemsPricingBreakdown & {
    basketItem: GetBasketItemWithError[];
    // The budget the item errors were computed with, so the cart caps quantities the same way.
    buyer: TicketBuyerLimits;
    appliedDiscountCodes: Array<{
      id: string;
      code: string;
      discount: Omit<Discount, "id" | "createdAt" | "updatedAt">;
      // Units the code may still discount for this visitor (null = unlimited), how many basket
      // units it discounted and how many eligible units it left at full price once exhausted.
      maxApplications: number | null;
      appliedUnits: number;
      skippedUnits: number;
    }>;
  };

export type GetBasketResume = {
  articleNumber: number;
  finalPrice: number;
  displayName: string;
  id: string;
  isExpired: boolean;
  expirationCountdown: Date;
};

export type BasketItemPreview = {
  name: string;
  eventName: string | null;
  unitPrice: number;
  quantity: number;
  totalPrice: number;
  totalPriceTtc: number;
};

export type CartOrder = {
  id: string;
  reference: string;
  status: Exclude<OrderStatus, "pending">;
};

export type CartMissingActivityTicket = {
  activityTicketQuantity: number;
  eventTicketQuantity: number;
  hasMultipleEvents?: boolean;
};

export const userShortTypeWithSocialsValues =
  Prisma.validator<Prisma.UserDefaultArgs>()({
    select: {
      id: true,
      firstName: true,
      lastName: true,
      email: true,
      phoneNumber: true,
      connectId: true,
      zipCode: true,
      picture: {
        select: {
          key: true,
        },
      },
      structure: {
        select: {
          id: true,
          businessName: true,
          socials: true,
        },
      },
    },
  });

export type UserShortTypeWithSocials = Prisma.UserGetPayload<
  typeof userShortTypeWithSocialsValues
>;

export const previewBasketTypeValues =
  Prisma.validator<Prisma.BasketDefaultArgs>()({
    select: {
      id: true,
      type: true,
      isExpired: true,
      user: {
        select: {
          id: true,
          firstName: true,
          lastName: true,
        },
      },
      discountCode: {
        select: {
          id: true,
          name: true,
          isQuantityInfinite: true,
          quantityLeft: true,
          maxUsesPerVisitor: true,
          discount: { select: { type: true, value: true } },
        },
        where: {
          disabledAt: null,
          deletedAt: null,
        },
      },
      basketItem: {
        select: {
          id: true,
          expirationCountdown: true,
        },
      },
      comments: {
        orderBy: { createdAt: "asc" },
        select: CommercialisationTaskCommentValues.select,
      },
      meetings: {
        select: CommercialisationMeetingValues.select,
        orderBy: {
          updatedAt: "desc",
        },
      },
    },
  });

export type PreviewBasket = Prisma.BasketGetPayload<
  typeof previewBasketTypeValues
>;

export type GetBasketWithUser = GetUserBasket & {
  user: UserShortTypeWithSocials | null;
};
